import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material'
import { Add, Check, DeleteOutline, Edit, Visibility } from '@mui/icons-material'
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { com } from '@eidu/form'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import usePermissionGrantRepository from '../../io/usePermissionGrantRepository'
import usePermittedActions from '../../io/usePermittedActions'
import PermissionError from '../entity/PermissionError'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import useEntitiesOfType from '../../io/useEntitiesOfType'
import { findEntityTypeByFieldId, findFieldById } from '../../util/EntityTypeExtensions'
import fieldValueLabel from '../../util/FieldValueExtensions'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import { getTypeDisplayName, parseTypeName } from '../../util/entityType/typeNameSchema'
import actionLabel from '../../domain/permission/actionLabel'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import EntitySelectorPermission = com.eidu.sharedlib.entity.permission.EntitySelectorPermission
import EntityPathPermission = com.eidu.sharedlib.entity.permission.EntityPathPermission
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import PermittedGlobalAction = com.eidu.sharedlib.entity.permission.PermittedGlobalAction
import Permission = com.eidu.sharedlib.entity.permission.Permission
import Action = com.eidu.sharedlib.entity.permission.Action
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'

type RowHeader =
  | { id: string; type: 'global'; label: string; action: Action }
  | { id: string; type: 'schema'; label: string }
  | { id: string; type: 'entity'; label: string; entityTypeId: EntityTypeId }

type SelectorOption = { typeId: string | null; scope: ActionScope }

const collectSelectorOptions = (selector: EntitySelector, entityTypes: readonly EntityType[]): SelectorOption[] => {
  if (EntitySelector.All.equals(selector)) return [{ typeId: null, scope: { all: true, limited: false } }]
  if (EntitySelector.None.equals(selector)) return []
  if (selector instanceof EntitySelector.OfType)
    return [{ typeId: selector.typeId.toString(), scope: { all: true, limited: false } }]
  if (
    selector instanceof EntitySelector.FieldValueEquals ||
    selector instanceof EntitySelector.FieldValueEqualsUserEntityId ||
    selector instanceof EntitySelector.FieldValueEqualsUserFieldValue
  ) {
    const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
    return entityType ? [{ typeId: entityType.id.toString(), scope: { all: false, limited: true } }] : []
  }
  if (selector instanceof EntitySelector.And || selector instanceof EntitySelector.Or)
    return [...selector.selectors.asJsReadonlyArrayView()].flatMap((item) => collectSelectorOptions(item, entityTypes))
  if (selector instanceof EntitySelector.Not) return [{ typeId: null, scope: { all: false, limited: true } }]
  return []
}

const collectSelectorFieldIds = (selector: EntitySelector): FieldId[] => {
  if (selector instanceof EntitySelector.FieldValueEquals) return [selector.fieldId]
  if (selector instanceof EntitySelector.FieldValueEqualsUserEntityId) return [selector.fieldId]
  if (selector instanceof EntitySelector.FieldValueEqualsUserFieldValue) return [selector.fieldId, selector.userFieldId]
  if (selector instanceof EntitySelector.And)
    return [...selector.selectors.asJsReadonlyArrayView()].flatMap(collectSelectorFieldIds)
  if (selector instanceof EntitySelector.Or)
    return [...selector.selectors.asJsReadonlyArrayView()].flatMap(collectSelectorFieldIds)
  if (selector instanceof EntitySelector.Not) return collectSelectorFieldIds(selector.selector)
  return []
}

type SelectorHeaderLabel = { primary: string; secondary?: string }
type CellCoordinate = { rowIndex: number; colIndex: number }

const isSelectableRow = (row: RowHeader) => row.type === 'global' || row.type === 'entity'
const cellKey = ({ rowIndex, colIndex }: CellCoordinate) => `${rowIndex}:${colIndex}`

const formatSelectorLabelText = (
  selector: EntitySelector,
  entityTypes: readonly EntityType[],
  entitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>
): string => {
  if (EntitySelector.All.equals(selector)) return 'All Users'
  if (selector instanceof EntitySelector.OfType) {
    const typeName = entityTypes.find((type) => type.id.equals(selector.typeId))?.name
    return typeName ? getTypeDisplayName(typeName) : `Unknown type (${selector.typeId})`
  }
  if (selector instanceof EntitySelector.FieldValueEquals) {
    const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
    if (!entityType) return `Unknown type (${selector.fieldId})`
    const field = findFieldById(entityType, selector.fieldId)
    if (!field) return getTypeDisplayName(entityType.name)
    const rawValue = fieldValueToString(selector.value)
    const label = fieldValueLabel(field, selector.value, entitiesByTypeId) ?? rawValue
    return `${getTypeDisplayName(entityType.name)}:${label}(${field.name})`
  }
  if (selector instanceof EntitySelector.And)
    return [...selector.selectors.asJsReadonlyArrayView()]
      .map((item) => formatSelectorLabelText(item, entityTypes, entitiesByTypeId))
      .join(' & ')
  if (selector instanceof EntitySelector.Or)
    return [...selector.selectors.asJsReadonlyArrayView()]
      .map((item) => formatSelectorLabelText(item, entityTypes, entitiesByTypeId))
      .join(' / ')
  if (selector instanceof EntitySelector.Not)
    return `NOT ${formatSelectorLabelText(selector.selector, entityTypes, entitiesByTypeId)}`
  return selector.toString()
}

const formatSelectorHeaderLabel = (
  selector: EntitySelector,
  entityTypes: readonly EntityType[],
  entitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>
): SelectorHeaderLabel => {
  if (EntitySelector.All.equals(selector)) return { primary: 'All Users' }
  if (selector instanceof EntitySelector.OfType) {
    const typeName = entityTypes.find((type) => type.id.equals(selector.typeId))?.name
    return { primary: typeName ? getTypeDisplayName(typeName) : `Unknown type (${selector.typeId})` }
  }
  if (selector instanceof EntitySelector.FieldValueEquals) {
    const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
    if (!entityType) return { primary: `Unknown type (${selector.fieldId})` }
    const field = findFieldById(entityType, selector.fieldId)
    if (!field) return { primary: getTypeDisplayName(entityType.name) }
    const rawValue = fieldValueToString(selector.value)
    const label = fieldValueLabel(field, selector.value, entitiesByTypeId) ?? rawValue
    return { primary: getTypeDisplayName(entityType.name), secondary: `${field.name}: ${label}` }
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserEntityId) {
    const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
    if (!entityType) return { primary: `Unknown type (${selector.fieldId})` }
    const field = findFieldById(entityType, selector.fieldId)
    if (!field) return { primary: getTypeDisplayName(entityType.name) }
    return { primary: getTypeDisplayName(entityType.name), secondary: `${field.name}: User` }
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserFieldValue) {
    const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
    if (!entityType) return { primary: `Unknown type (${selector.fieldId})` }
    const field = findFieldById(entityType, selector.fieldId)
    if (!field) return { primary: getTypeDisplayName(entityType.name) }
    const userEntityType = findEntityTypeByFieldId(entityTypes, selector.userFieldId)
    const userField = userEntityType ? findFieldById(userEntityType, selector.userFieldId) : undefined
    const userFieldLabel =
      userField && userEntityType
        ? `${getTypeDisplayName(userEntityType.name)}.${userField.name}`
        : selector.userFieldId.toString()
    return { primary: getTypeDisplayName(entityType.name), secondary: `${field.name}: ${userFieldLabel}` }
  }
  return { primary: formatSelectorLabelText(selector, entityTypes, entitiesByTypeId) }
}

const normalizeSortValue = (value: string) => value.trim().toLowerCase()

const selectorHeaderSortKey = (label: SelectorHeaderLabel) => {
  const primary = normalizeSortValue(label.primary)
  let fieldName = ''
  let fieldValue = ''
  if (label.secondary) {
    const separatorIndex = label.secondary.indexOf(':')
    if (separatorIndex >= 0) {
      fieldName = label.secondary.slice(0, separatorIndex).trim()
      fieldValue = label.secondary.slice(separatorIndex + 1).trim()
    } else {
      fieldValue = label.secondary.trim()
    }
  }
  return {
    primary,
    fieldName: normalizeSortValue(fieldName),
    fieldValue: normalizeSortValue(fieldValue),
  }
}

const selectorKey = (selector: EntitySelector, entityTypes: readonly EntityType[]): string => {
  if (EntitySelector.All.equals(selector)) return 'all'
  if (selector instanceof EntitySelector.OfType) return `type:${selector.typeId.toString()}`
  if (selector instanceof EntitySelector.FieldValueEquals) {
    const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
    const typeKey = entityType ? entityType.id.toString() : 'unknown'
    const valueKey = fieldValueToString(selector.value)
    return `field:${typeKey}:${selector.fieldId.toString()}:${valueKey}`
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserEntityId)
    return `field-user-entity:${selector.fieldId.toString()}`
  if (selector instanceof EntitySelector.FieldValueEqualsUserFieldValue)
    return `field-user-field:${selector.fieldId.toString()}:${selector.userFieldId.toString()}`
  if (selector instanceof EntitySelector.And)
    return `and:${[...selector.selectors.asJsReadonlyArrayView()]
      .map((item) => selectorKey(item, entityTypes))
      .join('|')}`
  if (selector instanceof EntitySelector.Or)
    return `or:${[...selector.selectors.asJsReadonlyArrayView()]
      .map((item) => selectorKey(item, entityTypes))
      .join('|')}`
  if (selector instanceof EntitySelector.Not) return `not:${selectorKey(selector.selector, entityTypes)}`
  return selector.toString()
}

const isCrudEntityAction = (action: EntityAction) =>
  action === EntityAction.CreateEntity ||
  action === EntityAction.ReadEntity ||
  action === EntityAction.UpdateEntity ||
  action === EntityAction.DeleteEntity

const CRUD_ACTIONS: { action: EntityAction; Icon: typeof Add }[] = [
  { action: EntityAction.CreateEntity, Icon: Add },
  { action: EntityAction.ReadEntity, Icon: Visibility },
  { action: EntityAction.UpdateEntity, Icon: Edit },
  { action: EntityAction.DeleteEntity, Icon: DeleteOutline },
]

type ActionScope = { all: boolean; limited: boolean }
type ActionAccessInfo = { total: number; all: number; limited: number }

const boldCheckSx: SxProps<Theme> = {
  stroke: 'currentColor',
  strokeWidth: 1.5,
}

const ActionIconIndicator = ({
  Icon,
  access,
  iconSx,
}: {
  Icon: typeof Add
  access: ActionAccessInfo | undefined
  iconSx?: SxProps<Theme>
}) => {
  if (!access || access.total === 0)
    return <Box sx={{ width: 20, height: 20, display: 'inline-flex', verticalAlign: 'middle' }} />
  const iconColor = 'text.primary'
  const showCount = !(access.total === 1 && access.all === 1)
  const iconStyles = iconSx
    ? Array.isArray(iconSx)
      ? [{ fontSize: 18, color: iconColor }, ...iconSx]
      : [{ fontSize: 18, color: iconColor }, iconSx]
    : { fontSize: 18, color: iconColor }

  return (
    <Box
      sx={{
        position: 'relative',
        width: 20,
        height: 20,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
      }}
    >
      <Icon sx={iconStyles} />
      {showCount ? (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            right: -3,
            top: -2,
            bgcolor: 'error.main',
            color: 'common.white',
            fontSize: 8,
            fontWeight: 700,
            lineHeight: 1,
            width: 12,
            height: 12,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {access.total}
        </Box>
      ) : null}
    </Box>
  )
}

const ColumnHeaderLabel = ({ label }: { label: SelectorHeaderLabel }) => (
  <Stack spacing={0} alignItems="center" textAlign="center">
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
      {label.primary}
    </Typography>
    {label.secondary ? (
      <Typography variant="caption" sx={{ fontWeight: 400 }}>
        {label.secondary}
      </Typography>
    ) : null}
  </Stack>
)

const NewAccessControlPage = () => {
  const entityTypeRepository = useContext(EntityTypesContext)
  const entityTypesMap = entityTypeRepository.useAll()
  const entityTypes = useMemo(() => [...(entityTypesMap?.values() ?? [])], [entityTypesMap])
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set())
  const selectedCellsRef = useRef(selectedCells)
  const selectionAnchorRef = useRef<CellCoordinate | null>(null)
  const [isSelecting, setIsSelecting] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  const permissionGrantRepository = usePermissionGrantRepository(200, [entityTypesMap?.size ?? 0])
  const permissionGrants = permissionGrantRepository.useAll()
  const loadingGrants = permissionGrantRepository.useLoading([permissionGrantRepository])
  const grantsError = permissionGrantRepository.useError()

  const requested = useRef([new PermittedGlobalAction(GlobalAction.ManagePermissionGrants)])
  const [permissions, , permissionsLoading] = usePermittedActions(requested.current)
  const hasManagePermissionGrantsPermission = permissions?.some(
    (permittedAction) => permittedAction.action === GlobalAction.ManagePermissionGrants
  )

  const userSelectors = useMemo(
    () => Array.from(permissionGrants?.values() ?? []).map((grant) => grant.permissionGrant.userSelector),
    [permissionGrants]
  )

  const selectorFieldIds = useMemo(() => userSelectors.flatMap(collectSelectorFieldIds), [userSelectors])

  const referencableEntityTypeIds = useMemo(() => {
    const ids = new Map<string, EntityTypeId>()
    selectorFieldIds.forEach((fieldId) => {
      const entityType = findEntityTypeByFieldId(entityTypes, fieldId)
      if (!entityType) return
      const field = findFieldById(entityType, fieldId)
      if (!field || field.type !== FieldType.Reference) return
      Array.from(field.validReferenceTypes?.asJsReadonlySetView() ?? []).forEach((typeId) => {
        ids.set(typeId.toString(), typeId)
      })
    })
    return Array.from(ids.values())
  }, [entityTypes, selectorFieldIds])

  const entitiesByTypeId =
    useEntitiesOfType(
      {
        typeIds: referencableEntityTypeIds,
        allTypes: entityTypesMap ?? new Map(),
        pageSize: 100,
      },
      [referencableEntityTypeIds.length, entityTypesMap?.size ?? 0]
    ) ?? new Map()

  const columnHeaders = useMemo(() => {
    const grouped = new Map<string, { label: SelectorHeaderLabel; permissions: Permission[] }>()
    Array.from(permissionGrants?.values() ?? []).forEach((grant) => {
      const selector = grant.permissionGrant.userSelector
      const key = selectorKey(selector, entityTypes)
      const label = formatSelectorHeaderLabel(selector, entityTypes, entitiesByTypeId)
      const entry = grouped.get(key) ?? { label, permissions: [] }
      entry.permissions.push(...grant.permissionGrant.permissions.asJsReadonlySetView())
      grouped.set(key, entry)
    })
    return Array.from(grouped.entries())
      .sort(([, a], [, b]) => {
        const left = selectorHeaderSortKey(a.label)
        const right = selectorHeaderSortKey(b.label)
        const primary = left.primary.localeCompare(right.primary, undefined, { sensitivity: 'base' })
        if (primary !== 0) return primary
        const fieldName = left.fieldName.localeCompare(right.fieldName, undefined, { sensitivity: 'base' })
        if (fieldName !== 0) return fieldName
        return left.fieldValue.localeCompare(right.fieldValue, undefined, { sensitivity: 'base' })
      })
      .map(([key, value]) => {
        const entityActionAccessByTypeId = new Map<string, Map<EntityAction, ActionAccessInfo>>()
        const entityActionAccessSummary = new Map<EntityAction, ActionAccessInfo>()
        const addAccessForTypeId = (typeId: string, action: EntityAction, scope: ActionScope) => {
          const actionMap = entityActionAccessByTypeId.get(typeId) ?? new Map<EntityAction, ActionAccessInfo>()
          const existing = actionMap.get(action) ?? { total: 0, all: 0, limited: 0 }
          actionMap.set(action, {
            total: existing.total + 1,
            all: existing.all + (scope.all ? 1 : 0),
            limited: existing.limited + (scope.limited ? 1 : 0),
          })
          entityActionAccessByTypeId.set(typeId, actionMap)
        }
        const addAccessSummary = (action: EntityAction, scope: ActionScope) => {
          const existing = entityActionAccessSummary.get(action) ?? { total: 0, all: 0, limited: 0 }
          entityActionAccessSummary.set(action, {
            total: existing.total + 1,
            all: existing.all + (scope.all ? 1 : 0),
            limited: existing.limited + (scope.limited ? 1 : 0),
          })
        }
        const applySelectorOption = (option: SelectorOption, action: EntityAction) => {
          addAccessSummary(action, option.scope)
          if (!isCrudEntityAction(action)) return
          if (option.typeId === null) {
            entityTypes.forEach((type) => addAccessForTypeId(type.id.toString(), action, option.scope))
            return
          }
          addAccessForTypeId(option.typeId, action, option.scope)
        }

        value.permissions.forEach((permission) => {
          if (permission instanceof EntitySelectorPermission) {
            const options = collectSelectorOptions(permission.targets, entityTypes)
            options.forEach((option) => applySelectorOption(option, permission.action))
          } else if (permission instanceof EntityPathPermission) {
            applySelectorOption(
              { typeId: permission.path.toType.toString(), scope: { all: false, limited: true } },
              permission.action
            )
          }
        })

        return {
          id: key,
          label: value.label,
          permissions: value.permissions,
          entityActionAccessByTypeId,
          entityActionAccessSummary,
        }
      })
  }, [permissionGrants, entityTypes, entitiesByTypeId])

  const rowHeaders = useMemo<RowHeader[]>(() => {
    const globalActions: Action[] = [
      ...GlobalAction.values(),
      EntityAction.SubmitForm,
      EntityAction.ViewFormSubmissions,
      EntityAction.ShareSchoolLogin,
    ]

    const rows: RowHeader[] = globalActions.map((action) => ({
      id: `global-${action}`,
      type: 'global',
      label: actionLabel(action),
      action,
    }))

    const grouped = new Map<string, { schema: string; types: EntityType[] }>()
    entityTypes.forEach((type) => {
      const parsed = parseTypeName(type.name)
      const schema = parsed.hasSchema && parsed.schema ? parsed.schema : 'Other'
      const entry = grouped.get(schema) ?? { schema, types: [] }
      entry.types.push(type)
      grouped.set(schema, entry)
    })

    const sortedGroups = [...grouped.values()].sort((a, b) =>
      a.schema.localeCompare(b.schema, undefined, { sensitivity: 'base' })
    )

    sortedGroups.forEach((group) => {
      rows.push({ id: `schema-${group.schema}`, type: 'schema', label: group.schema })
      group.types
        .slice()
        .sort((a, b) =>
          getTypeDisplayName(a.name).localeCompare(getTypeDisplayName(b.name), undefined, { sensitivity: 'base' })
        )
        .forEach((type) =>
          rows.push({
            id: `type-${type.id.toString()}`,
            type: 'entity',
            label: getTypeDisplayName(type.name),
            entityTypeId: type.id,
          })
        )
    })

    return rows
  }, [entityTypes])

  const updateSelection = useCallback(
    (target: CellCoordinate) => {
      const anchor = selectionAnchorRef.current
      if (!anchor || columnHeaders.length === 0) return
      const anchorRow = rowHeaders[anchor.rowIndex]
      if (!anchorRow || !isSelectableRow(anchorRow)) return
      const anchorRowType = anchorRow.type
      const minRow = Math.min(anchor.rowIndex, target.rowIndex)
      const maxRow = Math.max(anchor.rowIndex, target.rowIndex)
      const minCol = Math.min(anchor.colIndex, target.colIndex)
      const maxCol = Math.max(anchor.colIndex, target.colIndex)
      const next = new Set<string>()
      for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex++) {
        const row = rowHeaders[rowIndex]
        if (!row || !isSelectableRow(row) || row.type !== anchorRowType) continue
        for (let colIndex = minCol; colIndex <= maxCol; colIndex++) {
          if (!columnHeaders[colIndex]) continue
          next.add(cellKey({ rowIndex, colIndex }))
        }
      }
      selectedCellsRef.current = next
      setSelectedCells(next)
    },
    [columnHeaders, rowHeaders]
  )

  const startSelection = useCallback(
    (event: MouseEvent, rowIndex: number, colIndex: number) => {
      if (dialogOpen) return
      if (event.button !== 0) return
      if (!rowHeaders[rowIndex] || !isSelectableRow(rowHeaders[rowIndex])) return
      event.preventDefault()
      setIsSelecting(true)
      const anchor = { rowIndex, colIndex }
      selectionAnchorRef.current = anchor
      const next = new Set<string>([cellKey(anchor)])
      selectedCellsRef.current = next
      setSelectedCells(next)
    },
    [dialogOpen, rowHeaders]
  )

  useEffect(() => {
    selectedCellsRef.current = selectedCells
  }, [selectedCells])

  useEffect(() => {
    if (!isSelecting) return
    const handleMouseUp = () => {
      setIsSelecting(false)
      selectionAnchorRef.current = null
      if (selectedCellsRef.current.size > 0) {
        setDialogOpen(true)
      }
    }
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [isSelecting])

  const closeDialog = useCallback(() => {
    setDialogOpen(false)
    setSelectedCells(new Set())
  }, [])

  if (permissionsLoading) {
    return <LoadingOverlay isOpen />
  }

  if (!hasManagePermissionGrantsPermission) {
    return <PermissionError missingPermittedActions={[GlobalAction.ManagePermissionGrants]} />
  }

  return (
    <Stack padding={3} spacing={2}>
      <Typography variant="h4">Access Control Viewer</Typography>
      <Collapse in={!!grantsError}>
        <Alert
          variant="filled"
          severity="error"
          sx={{ marginBottom: 2 }}
          onClose={() => permissionGrantRepository.clearError()}
        >
          {String(grantsError)}
        </Alert>
      </Collapse>
      <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 220, borderRight: 1, borderColor: 'divider' }} />
              {columnHeaders.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ minWidth: 100, borderRight: 1, borderColor: 'divider', textAlign: 'center' }}
                >
                  <ColumnHeaderLabel label={column.label} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowHeaders.map((row, rowIndex) => (
              <TableRow key={row.id}>
                <TableCell
                  sx={{
                    fontWeight: row.type === 'schema' ? 600 : 400,
                    color: row.type === 'schema' ? 'text.secondary' : 'text.primary',
                    pl: row.type === 'entity' ? 3 : 1,
                    whiteSpace: 'nowrap',
                    borderRight: 1,
                    borderColor: 'divider',
                    bgcolor: row.type === 'schema' ? 'action.hover' : 'transparent',
                  }}
                >
                  {row.label}
                </TableCell>
                {columnHeaders.map((column, colIndex) => {
                  const selected = selectedCells.has(cellKey({ rowIndex, colIndex }))
                  const selectable = isSelectableRow(row)
                  const baseBg = row.type === 'schema' ? 'action.hover' : 'transparent'
                  const cellBg = selected ? 'action.selected' : baseBg
                  return (
                    <TableCell
                      key={`${row.id}-${column.id}`}
                      sx={{
                        borderRight: 1,
                        borderColor: 'divider',
                        textAlign: 'center',
                        bgcolor: cellBg,
                        cursor: selectable ? (isSelecting ? 'grabbing' : 'pointer') : 'default',
                        userSelect: selectable ? 'none' : 'auto',
                      }}
                      onMouseDown={selectable ? (event) => startSelection(event, rowIndex, colIndex) : undefined}
                      onMouseEnter={
                        selectable && isSelecting ? () => updateSelection({ rowIndex, colIndex }) : undefined
                      }
                    >
                      {row.type === 'global' ? (
                        row.action instanceof EntityAction ? (
                          <ActionIconIndicator
                            Icon={Check}
                            access={column.entityActionAccessSummary.get(row.action)}
                            iconSx={boldCheckSx}
                          />
                        ) : column.permissions.some((permission) => permission.action === row.action) ? (
                          <Check fontSize="small" sx={boldCheckSx} />
                        ) : null
                      ) : row.type === 'entity' ? (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {CRUD_ACTIONS.map(({ action, Icon }) => (
                            <ActionIconIndicator
                              key={action.toString()}
                              Icon={Icon}
                              access={column.entityActionAccessByTypeId.get(row.entityTypeId.toString())?.get(action)}
                            />
                          ))}
                        </Stack>
                      ) : null}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit access rights</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Selected fields: {selectedCells.size}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={closeDialog}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <LoadingOverlay isOpen={permissionsLoading || loadingGrants || !permissionGrants} />
    </Stack>
  )
}

export default NewAccessControlPage
